import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getCurrentUserIsGuest,
    getSession,
    getSettingsCollection,
} from "@insite/client-framework/Store/Context/ContextSelectors";

import { getLocation, getReturnUrl } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType, useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SearchInput from "@insite/content-library/Widgets/Header/SearchInput";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { HasHistory, useHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useState } from "react";
import { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";
import ArrowRightWhiteIcon from "@tCentro/Icons/ArrowRightWhiteIcon";
import Icon from "@insite/mobius/Icon";

const enum fields {
    content = "content",
    loginbuttonTextField = "loginbuttonTextField",
    createAccountbuttonTextField = "createAccountbuttonTextField",
}
const mapStateToProps = (state: ApplicationState) => {
    const { search } = getLocation(state);
    const accountSettings = getSettingsCollection(state).accountSettings;
    const returnUrl = getReturnUrl(state);
    const shippingPageUrl = getPageLinkByPageType(state, "CheckoutShippingPage")?.url;
    const referredFromShipping = returnUrl === shippingPageUrl;
    const createAccountPageLink = getPageLinkByPageType(state, "CreateAccountPage");

    return {
        allowCreateAccount: accountSettings.allowCreateAccount,
        allowGuestCheckout: accountSettings.allowGuestCheckout && !getCurrentUserIsGuest(state) && referredFromShipping,
        returnUrl,
        createAccountUrl: createAccountPageLink ? `${createAccountPageLink.url}${search}` : undefined,
        signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        currentLocation: getLocation(state),
        userName: state.context.session.userName,
        currentUserIsGuest: getCurrentUserIsGuest(state),
    };
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.content]: string;
        [fields.loginbuttonTextField]: string;
        [fields.createAccountbuttonTextField]: string;
    };
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasToasterContext & HasHistory;

export interface HomePageBlockStyles {
    container?: InjectableCss;
    contentSyles?: TypographyProps;
    loginBtnStyles?: ButtonPresentationProps;
    createAccountBtnStyles?: ButtonPresentationProps;
    buttonMainContainer?: InjectableCss;
}

export const homePageBlockStyles: HomePageBlockStyles = {
    container: {
        css: css`
            display: flex;
            margin-bottom: 64px;
            margin-left: 0px;
            justify-content: center;
            gap: 10px;
            @media (min-width: 992px) and (max-width: 1200px) {
                flex-direction: column;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-direction: column;
                    `,
                    css`
                        flex-direction: column;
                    `,
                    css`
                        flex-direction: column;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    contentSyles: {
        css: css`
            font-size: 15px;
            width: 70%;
            font-weight: 400;
            line-height: 28px;
            color: #f2f2f2;
            text-align: center;
            width: unset;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: 100%;
                        font-size: 12px;
                        line-height: 20px;
                    `,
                    css`
                        width: 100%;
                        font-size: 12px;
                        line-height: 20px;
                    `,
                    css`
                        width: 100%;
                        font-size: 14px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    loginBtnStyles: {
        css: css`
            position: relative;
            font-size: 15px;
            font-weight: 600;
            span {
                position: absolute;
                margin-left: 5px;
                svg {
                    height: 11px;
                    width: 18px;
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: 12px;
                        span {
                            display: initial;
                            padding: 2px;
                        }
                    `,
                    css`
                        font-size: 12px;
                        span {
                            display: initial;
                            padding: 2px;
                        }
                    `,
                    css`
                        font-size: 14px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    createAccountBtnStyles: {
        css: css`
            position: relative;
            font-size: 15px;
            font-weight: 600;
            span {
                position: absolute;
                margin-left: 6px;
                svg {
                    height: 11px;
                    width: 18px;
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: 12px;
                        span {
                            padding: 4px;
                            align-items: initial;
                        }
                    `,
                    css`
                        font-size: 12px;
                        span {
                            padding: 4px;
                            align-items: initial;
                        }
                    `,
                    css`
                        font-size: 14px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    buttonMainContainer: {
        css: css`
            display: flex;
            gap: 40px;
            padding: 3px 6px;
            cursor: pointer;
            @media (min-width: 992px) and (max-width: 1200px) {
                justify-content: center;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        justify-content: center;
                    `,
                    css`
                        justify-content: center;
                    `,
                    css`
                        justify-content: center;
                    `,
                    null,
                    null,
                ])}
        `,
    },
};

const styles = homePageBlockStyles;

const CENHomePageBlock: FC<Props> = ({
    fields,

    id,
    createAccountUrl,
    history,
    toaster,
    signInUrl,
    userName,
    currentUserIsGuest,
    currentLocation,
}) => {
    const handleCreateNewAccount = () => {
        if (!createAccountUrl) {
            return;
        }

        history.push(createAccountUrl);
    };

    const onSignInHandler = (e: React.MouseEvent) => {
        if (!signInUrl) {
            logger.warn("No url was found for SignInPage, defaulting to /SignIn");
            history.push("/MyAccount/SignIn");
        } else {
            const returnUrl = currentLocation.pathname + currentLocation.search;
            if (returnUrl === "/") {
                history.push(`${signInUrl}`);
            } else {
                history.push(`${signInUrl}?returnUrl=${encodeURIComponent(returnUrl)}&clientRedirect=true`);
            }
        }
        e.preventDefault();
    };

    return (
        <>
            {!(!userName || (userName && currentUserIsGuest)) ? (
                <StyledWrapper
                    css={css`
                        padding-bottom: 64px;
                    `}
                ></StyledWrapper>
            ) : (
                <StyledWrapper {...styles.container}>
                    <Typography {...styles.contentSyles}>{fields.content}</Typography>
                    <StyledWrapper {...styles.buttonMainContainer}>
                        <StyledWrapper {...styles.loginBtnStyles} onClick={onSignInHandler}>
                            {fields.loginbuttonTextField}
                            <Icon src={ArrowRightWhiteIcon} />
                        </StyledWrapper>
                        <StyledWrapper {...styles.createAccountBtnStyles} onClick={handleCreateNewAccount}>
                            {fields.createAccountbuttonTextField}
                            <Icon src={ArrowRightWhiteIcon} />
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: withHistory(connect(mapStateToProps)(CENHomePageBlock)),
    definition: {
        group: "Common",
        displayName: "CENHomePageBlock",
        fieldDefinitions: [
            {
                name: fields.content,
                editorTemplate: "TextField",
                displayName: "Content",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.loginbuttonTextField,
                editorTemplate: "TextField",
                displayName: "Button 1",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.createAccountbuttonTextField,
                editorTemplate: "TextField",
                displayName: "Button 2",
                defaultValue: "",
                fieldType: "General",
            },
        ],
    },
};
export default widgetModule;
